import React, { useMemo } from 'react';
import * as S from './DeleteLog.styled';
import EscapeButton from 'pages/components/EscapeButton/EscapeButton';
import { Log } from '../hooks';
import { useAppSelector } from 'redux/hooks';
import { selectCurrency } from 'pages/redux';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { convertDate } from 'utils';
import { useSettings } from 'pages/Settings/hooks';
import {
  buildStaffCodeByActorToken,
  buildStaffMaps,
  resolveActorDisplayName,
} from '../logActor';
import {
  buildCustomerMaps,
  resolveCustomerDisplayName,
} from '../logCustomer';
import { toPlainLogMessage } from '../logFormat';
import { selectCustomers } from 'pages/Settings/redux';
import { selectErpRecords } from 'pages/Records/redux';
import { displayActivityLogType } from '../activityLogLabels';

/** Strip message markup for a compact read-only preview (never show raw activity UUIDs). */
const plainMessage = (
  raw: string,
  currency: string,
  resolveActor: (token: string) => string,
  resolveCustomer: (token: string) => string,
) => toPlainLogMessage(raw, {
  resolveActor,
  resolveCustomer,
  formatPrice: (v) => `${currency}${Number(v).toLocaleString()}`,
});

const PreviewDeletedLog = ({
  onExit,
  snapshot,
  activityLogId,
}: {
  onExit: Function,
  /** Deleted row snapshot from Activity deleted.logTable (id is a UUID string). */
  snapshot: Log,
  /** UUID from the clicked &L&…&L& token (string — never treat as a number). */
  activityLogId?: string,
}) => {
  const currency = useAppSelector(selectCurrency);
  const customers = useAppSelector(selectCustomers);
  const { users } = useSettings();
  const erpRecords = useAppSelector(selectErpRecords);
  const staffMaps = useMemo(() => buildStaffMaps(users), [users]);
  const staffCodeByToken = useMemo(
    () => buildStaffCodeByActorToken(users, erpRecords || []),
    [users, erpRecords],
  );
  const customerMaps = useMemo(() => buildCustomerMaps(customers), [customers]);

  const amount = snapshot?.value;
  const amountLabel = amount != null && amount !== 'N/A' && !Number.isNaN(Number(amount))
    ? `${currency}${Number(amount).toLocaleString()}`
    : (amount || 'N/A');
  const typeLabel = displayActivityLogType(snapshot?.type);
  const dateLabel = snapshot?.date
    ? convertDate(new Date(snapshot.date)).replace('. 00:00am', '').replace('00:00am', '').trim()
    : null;
  const message = snapshot?.message
    ? plainMessage(
      String(snapshot.message),
      currency,
      (token) => resolveActorDisplayName(token, staffMaps, token, staffCodeByToken),
      (token) => resolveCustomerDisplayName(token, customerMaps, token),
    )
    : '';

  // Snapshot / click ids are UUID strings — only used as opaque keys, never shown.
  const hasActivityRef = Boolean(String(activityLogId || snapshot?.id || '').trim());

  return (
    <S.Container>
      <EscapeButton onClick={() => onExit()} />
      <S.Header>
        <S.PreviewIcon aria-hidden>
          <VisibilityOutlined fontSize="inherit" />
        </S.PreviewIcon>
        <S.MainLabel>Deleted log preview</S.MainLabel>
        <S.SubLabel>
          Snapshot of the entry before it was removed.
        </S.SubLabel>
      </S.Header>
      <S.EntryCard>
        <S.EntryCardLabel>Amount</S.EntryCardLabel>
        <S.EntryAmount>{amountLabel}</S.EntryAmount>
        <S.EntryMeta>
          {typeLabel}
          {hasActivityRef ? (
            <>
              {' · '}
              <S.EntryId>activity</S.EntryId>
            </>
          ) : null}
          {dateLabel ? (
            <>
              <br />
              {dateLabel}
            </>
          ) : null}
        </S.EntryMeta>
        {message ? (
          <S.PreviewMessage title={message}>
            {message}
          </S.PreviewMessage>
        ) : null}
      </S.EntryCard>
      <S.Actions>
        <S.SecondaryBtn type="button" onClick={() => onExit()}>
          Close
        </S.SecondaryBtn>
      </S.Actions>
    </S.Container>
  );
};

export default PreviewDeletedLog;
